import { useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { hasPermission, hasAnyPermission, hasAllPermissions, hasRole } from '../utils/permissions';

/**
 * Hook personalizado para verificar permisos del usuario autenticado
 * @returns Objeto con funciones para verificar permisos
 */
const usePermissions = () => {
    const { auth } = useContext(AuthContext);

    /**
     * Verifica si el usuario tiene un permiso específico
     * @param permission - Nombre del permiso a verificar
     * @returns true si el usuario tiene el permiso
     */
    const can = (permission: string): boolean => {
        return hasPermission(auth, permission);
    };

    /**
     * Verifica si el usuario tiene al menos uno de los permisos especificados
     * @param permissions - Array de permisos a verificar
     * @returns true si el usuario tiene al menos uno de los permisos
     */
    const canAny = (permissions: string[]): boolean => {
        return hasAnyPermission(auth, permissions);
    };

    /**
     * Verifica si el usuario tiene todos los permisos especificados
     * @param permissions - Array de permisos a verificar
     * @returns true si el usuario tiene todos los permisos
     */
    const canAll = (permissions: string[]): boolean => {
        return hasAllPermissions(auth, permissions);
    };

    /**
     * Verifica si el usuario tiene un rol específico
     * @param role - Nombre del rol a verificar
     * @returns true si el usuario tiene el rol
     */
    const isRole = (role: string): boolean => {
        return hasRole(auth, role);
    };

    return {
        can,
        canAny,
        canAll,
        isRole,
        auth
    };
};

export default usePermissions;

